import { useRef, useCallback, Suspense, lazy, useEffect } from 'react';
import { MdMyLocation, MdCenterFocusStrong, MdLayers } from 'react-icons/md';

const MapView = lazy(() => import('../MapView'));

/**
 * Wrapper around MapView: sticky map container with overlay controls
 * (zoom, locate, recenter, optional "Search as I move" toggle).
 * isVisible: when true and container was previously hidden, call invalidateSize
 * so Leaflet mapa na mobilnom prikaže tiles umesto sive površine.
 */
export default function MapPanel({
  items = [],
  isParent,
  userAddress,
  friends = [],
  selectedItemId,
  hoveredItemId,
  onMapMove,
  onMarkerClick,
  onMarkerHover,
  searchAsIMove,
  getItemHref,
  showSearchThisArea = false,
  onSearchThisArea,
  onRecenter,
  className = '',
  showMapFooter = true,
  markerHoverTooltipOnly = false,
  disableTooltips = false,
  isVisible = true,
}) {
  const mapRef = useRef(null);

  // Na mobilnom: kad mapa postane vidljiva (npr. prelazak sa Liste na Mapu),
  // Leaflet mora invalidateSize da prikaže tiles umesto sive površine
  useEffect(() => {
    if (!isVisible) return;
    const t = setTimeout(() => {
      mapRef.current?.invalidateSize?.();
    }, 100);
    return () => clearTimeout(t);
  }, [isVisible]);

  const handleRecenter = useCallback(() => {
    onRecenter?.();
    mapRef.current?.fitResults?.();
  }, [onRecenter]);

  return (
    <div className={`relative h-full min-h-0 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg ${className}`}>
      <Suspense
        fallback={
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-500 border-t-transparent" />
          </div>
        }
      >
        <MapView
          ref={mapRef}
          items={items}
          isParent={isParent}
          userAddress={userAddress}
          friends={friends}
          highlightedItemId={hoveredItemId}
          selectedItemId={selectedItemId}
          onMapMove={onMapMove}
          onMarkerClick={onMarkerClick}
          onMarkerHover={onMarkerHover}
          searchAsIMove={searchAsIMove}
          getItemHref={getItemHref}
          showFooter={showMapFooter}
          markerHoverTooltipOnly={markerHoverTooltipOnly}
          disableTooltips={disableTooltips}
        />
      </Suspense>

      {/* Overlay controls: zoom, locate, recenter */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2 z-[1000]">
        <button
          type="button"
          onClick={handleRecenter}
          className="p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          title="Centriraj na rezultate"
          aria-label="Centriraj na rezultate"
        >
          <MdCenterFocusStrong className="text-xl text-gray-700 dark:text-gray-300" />
        </button>
        <button
          type="button"
          onClick={() => {
            const map = mapRef.current?.getMap?.();
            if (map && navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  map.setView([pos.coords.latitude, pos.coords.longitude], 14);
                },
                () => {},
                { enableHighAccuracy: true }
              );
            }
          }}
          className="p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          title="Moja lokacija"
          aria-label="Moja lokacija"
        >
          <MdMyLocation className="text-xl text-primary-600 dark:text-primary-400" />
        </button>
      </div>

      {/* CTA: Search this area (appears after move when auto-search is OFF) */}
      {showSearchThisArea && !searchAsIMove && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1000] px-3">
          <button
            type="button"
            onClick={onSearchThisArea}
            className="px-4 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-lg border border-primary-500/30"
          >
            Pretraži ovu oblast
          </button>
        </div>
      )}
    </div>
  );
}
